/**
 * commentary-follow.service.ts — DLITE-3849
 *
 * The "follow a chip → hyperlink to the row" flow. When the user clicks an
 * entity chip on an expanded thread, this service emits exactly the SINGLE
 * entity ref the user clicked (`detail.target`) on `follow$`. Consumers focus
 * the owning panel + that one row.
 *
 * The backing subject is a ReplaySubject(1): a chip may target a panel that
 * is not open yet, so app.component opens it and the grid subscribes only
 * after it mounts — by then a plain Subject's synchronous emission would be
 * gone. Replaying the last target lets the just-mounted grid still focus its
 * row. Grids dedupe the replayed target by reference so a remount (tab
 * switch) does not re-focus a stale target.
 *
 * Listens on the injected DOCUMENT for the `commentary-thread-jump`
 * CustomEvent (composed: true crosses the commentary-ui Shadow DOM
 * boundary). `start()` is gated/idempotent so the root-provided singleton
 * only wires its listener once, when a consumer with commentary access
 * opts in.
 */
import { DOCUMENT } from '@angular/common'
import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Observable, ReplaySubject, Subscription, fromEvent } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import {
  COMMENTARY_THREAD_JUMP_EVENT,
  CommentaryEntityRef,
  CommentaryFollowTarget,
  ThreadJumpEvent,
} from '../types/thread-jump'

@Injectable({ providedIn: 'root' })
export class CommentaryFollowService implements OnDestroy {
  private readonly subject = new ReplaySubject<CommentaryFollowTarget>(1)
  readonly follow$: Observable<CommentaryFollowTarget> =
    this.subject.asObservable()
  private sub: Subscription | null = null

  constructor(@Inject(DOCUMENT) private readonly doc: Document) {}

  start(): void {
    if (this.sub) return
    this.sub = fromEvent<ThreadJumpEvent>(
      this.doc,
      COMMENTARY_THREAD_JUMP_EVENT,
    )
      .pipe(
        filter(
          ev =>
            !!ev.detail?.target &&
            !!ev.detail.target.type &&
            ev.detail.target.id != null,
        ),
        map((ev): CommentaryFollowTarget => {
          const threadAccountId = ev.detail.thread?.linkedEntities?.find(
            (e: CommentaryEntityRef) => e.type === 'account',
          )?.id
          return threadAccountId == null
            ? ev.detail.target
            : { ...ev.detail.target, threadAccountId }
        }),
      )
      .subscribe(t => this.subject.next(t))
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe()
    this.sub = null
  }
}
